/*
 * @Author: Ryan
 * @Date: 2026-02-02
 * @Description: How-to section showing image translation workflow
 */
import Image from 'next/image';
import React from 'react';

interface StepCardProps {
  index: number;
  title: string;
  description: string;
}

const steps = [
  {
    title: 'Upload Your Image',
    description:
      'Drag and drop your image or select it from your device. FalcoCut supports JPG, PNG, and WEBP formats for photos, screenshots, and scanned pages.',
  },
  {
    title: 'Select Target Language',
    description:
      'Choose the language you want to translate into. FalcoCut automatically detects the source language for you.',
  },
  {
    title: 'Review the Translated Text',
    description:
      'Click "Start" to see instant results. Review the translated text and confirm it matches your intended meaning before finalizing.',
  },
  {
    title: 'Finalize and Save Your Image',
    description:
      'Download or share the final image once the translation is confirmed. FalcoCut preserves the original layout and design while applying your translation.',
  },
];

const StepCard: React.FC<StepCardProps> = ({ index, title, description }) => {
  return (
    <div className="flex gap-4 rounded-2xl p-4 pad:p-5">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#6366F1] text-white flex items-center justify-center font-semibold text-sm">
        {index}
      </div>
      <div>
        <h3 className="text-base pad:text-lg font-semibold text-gray-900">{title}</h3>
        <p className="text-xs pad:text-sm text-gray-600 mt-1">{description}</p>
      </div>
    </div>
  );
};

const HowToUseSection: React.FC = () => {
  return (
    <section className="w-full py-[90px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-gray-900 mt-4 mb-8 text-center">
          How to Translate an Image with FalcoCut
        </h2>

        <div className="max-w-[1010px] mx-auto flex flex-col sm:flex-row items-center gap-8 pad:gap-10 sm:gap-12">
          {/* Visual */}
          <div className="flex-1">
            <div className="rounded-2xl overflow-hidden">
              <Image
                src="https://assets.falcocut.ai/ai-image-translator/how-to-translate-image-step-by-step.webp"
                alt="How to Translate an Image with FalcoCut"
                width={440}
                height={520}
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </div>
          </div>
          {/* Steps */}
          <div className="flex-1 w-full">
            <div className="space-y-4">
              {steps.map((step, index) => (
                <StepCard
                  key={step.title}
                  index={index + 1}
                  title={step.title}
                  description={step.description}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowToUseSection;
